import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import { isAmountSignUnexpectedForAccount } from "../../lib/account-flow";
import { formatAmount } from "../../lib/format-utils";
import { selectSmartLink } from "../../lib/smart-link";
import { flattenChartAccounts } from "../../lib/chart-utils";
import { getReceivableRemainingAmount } from "../../../lib/receivables";
import type {
  Account,
  BankTransaction,
  BankTransactionLink,
  BankTransactionSuggestionJournalEntry,
  BankTransactionSuggestionsResponse,
  ChartOfAccountsResponse,
  ChannelId,
} from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Input } from "../input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

const formatEditableAmount = (amount: number) => {
  if (!Number.isFinite(amount)) {
    return "";
  }
  return (Math.round(amount * 100) / 100).toFixed(2);
};

export const LinkModal = ({
  houseId,
  transaction,
  currencyFallback,
  onClose,
  onUpdated,
  isReadonly,
}: {
  houseId: number;
  transaction: BankTransaction;
  currencyFallback: string | null;
  onClose: () => void;
  onUpdated: () => void;
  isReadonly: boolean;
}) => {
  const { t } = useI18n();
  type LinkTab = "suggested" | "links" | "journal-entry";

  const [links, setLinks] = useState<BankTransactionLink[]>([]);
  const [suggestions, setSuggestions] = useState<BankTransactionSuggestionJournalEntry[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState("");
  const [notes, setNotes] = useState("");
  const [channel, setChannel] = useState<ChannelId | null>(null);
  const [channelLabel, setChannelLabel] = useState<string | null>(null);
  const [channelBalance, setChannelBalance] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<LinkTab>("suggested");
  const [linkError, setLinkError] = useState<string | null>(null);
  const [journalEntryError, setJournalEntryError] = useState<string | null>(null);
  const [journalEntryAmount, setJournalEntryAmount] = useState("");
  const [hasEditedAmount, setHasEditedAmount] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadLinks = async () => {
    setIsLoading(true);
    setLinkError(null);
    try {
      const response = await fetchJson<{ links: BankTransactionLink[] }>(
        `/api/houses/${houseId}/bank-transactions/${transaction.id}/links`,
      );
      setLinks(response.links);
    } catch (loadError) {
      setLinkError(
        loadError instanceof Error
          ? loadError.message
          : t("bankTransactions.linksLoadFailed", "Failed to load links"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadSuggestions = async () => {
    setIsLoading(true);
    setLinkError(null);
    try {
      const response = await fetchJson<BankTransactionSuggestionsResponse>(
        `/api/houses/${houseId}/bank-transactions/${transaction.id}/suggestions`,
      );
      setChannel(response.channel);
      setChannelLabel(response.channelLabel);
      setChannelBalance(response.channelBalance);
      setSuggestions(response.journalEntries);
      setSelectedIds(new Set());
    } catch (loadError) {
      setLinkError(
        loadError instanceof Error
          ? loadError.message
          : t("bankTransactions.suggestionsLoadFailed", "Failed to load suggestions"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadAccounts = async () => {
    setIsLoadingAccounts(true);
    setJournalEntryError(null);
    try {
      const response = await fetchJson<ChartOfAccountsResponse>(
        `/api/houses/${houseId}/chart-of-accounts`,
      );
      const flattened = flattenChartAccounts(response.accounts);
      const availableAccounts: Account[] = flattened
        .map((row) => row.node)
        .filter((node) => Boolean(node.id) && !node.isTotal)
        .map((node) => ({
          id: node.id ?? 0,
          houseId,
          name: node.name,
          labelKey: node.labelKey,
          number: node.code,
          currency: node.currency,
          category: "detail",
        }));
      setAccounts(availableAccounts);
    } catch (loadError) {
      setJournalEntryError(
        loadError instanceof Error
          ? loadError.message
          : t("bankTransactions.loadAccountsFailed", "Failed to load accounts"),
      );
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  useEffect(() => {
    void loadLinks();
    void loadSuggestions();
    setAccountId("");
    setNotes("");
    setJournalEntryError(null);
    setLinkError(null);
    setJournalEntryAmount(formatEditableAmount(transaction.amount));
    setHasEditedAmount(false);
    setActiveTab(transaction.amount > 0 ? "suggested" : "journal-entry");
  }, [transaction.amount, transaction.id]);

  useEffect(() => {
    void loadAccounts();
  }, [houseId]);

  const linkedTotal = links.reduce((sum, link) => sum + link.amount, 0);
  const remaining = transaction.amount - linkedTotal;

  const selectedTotal = useMemo(
    () =>
      suggestions.reduce(
        (sum, entry) => (selectedIds.has(entry.id) ? sum + entry.remainingAmount : sum),
        0,
      ),
    [selectedIds, suggestions],
  );
  const assignedToTransaction = Math.min(selectedTotal, transaction.amount);
  const remainingTransaction = Math.max(transaction.amount - selectedTotal, 0);
  const balanceApplied = Math.max(selectedTotal - transaction.amount, 0);
  const availableTotal = transaction.amount + Math.max(channelBalance, 0);
  const availableRemaining = availableTotal - selectedTotal;
  const isTransactionPositive = transaction.amount > 0;
  const amountEpsilon = 0.01;
  const journalDate = transaction.transactionDate ?? "";
  const currency = transaction.currency ?? currencyFallback ?? "DKK";

  useEffect(() => {
    if (hasEditedAmount) {
      return;
    }
    setJournalEntryAmount(formatEditableAmount(remaining));
  }, [hasEditedAmount, remaining]);
  const formatAccountLabel = (link: BankTransactionLink) => {
    const accountNumber = link.accountNumber?.trim();
    const accountName = link.accountLabelKey
      ? t(link.accountLabelKey, link.accountName ?? "").trim()
      : link.accountName?.trim();
    if (accountNumber && accountName) {
      return `${accountNumber} · ${accountName}`;
    }
    if (accountNumber) {
      return accountNumber;
    }
    if (accountName) {
      return accountName;
    }
    return t("entries.noAccount", "No account");
  };

  const handleSmartLink = () => {
    if (isReadonly) {
      return;
    }
    if (!channel) {
      setLinkError(
        t("bankTransactions.channelNotDetected", "Channel could not be detected from the transaction."),
      );
      return;
    }
    if (!isTransactionPositive) {
      setLinkError(
        t("bankTransactions.positiveOnly", "Only positive transactions can be linked to receivables."),
      );
      return;
    }
    if (!suggestions.length) {
      setLinkError(
        t("bankTransactions.noReceivables", "No receivable journal entries available for linking."),
      );
      return;
    }
    const { candidateSuggestions, selection } = selectSmartLink({
      suggestions,
      transactionAmount: transaction.amount,
      transactionDate: transaction.transactionDate,
      transactionCreatedAt: transaction.createdAt,
      channelBalance,
      cutoffDays: 60,
    });
    if (!candidateSuggestions.length) {
      setLinkError(
        t("bankTransactions.noReceivables", "No receivable journal entries available for linking."),
      );
      return;
    }
    if (!selection) {
      setLinkError(t("bankTransactions.noSmartLink", "No exact link found for smart link."));
      return;
    }
    setSelectedIds(new Set(selection.ids));
    setLinkError(null);
  };

  const toggleEntry = (entry: BankTransactionSuggestionJournalEntry) => {
    if (isReadonly) {
      return;
    }
    const next = new Set(selectedIds);
    if (next.has(entry.id)) {
      next.delete(entry.id);
      setSelectedIds(next);
      setLinkError(null);
      return;
    }
    const nextTotal = selectedTotal + entry.remainingAmount;
    if (!isTransactionPositive) {
      setLinkError(
        t("bankTransactions.positiveOnly", "Only positive transactions can be linked to receivables."),
      );
      return;
    }
    if (nextTotal - availableTotal > amountEpsilon) {
      setLinkError(
        t(
          "bankTransactions.selectionExceeds",
          "Selection exceeds available amount including channel balance.",
        ),
      );
      return;
    }
    next.add(entry.id);
    setSelectedIds(next);
    setLinkError(null);
  };

  const linkSelected = async () => {
    if (isReadonly) {
      return;
    }
    if (!channel) {
      setLinkError(
        t("bankTransactions.channelNotDetected", "Channel could not be detected from the transaction."),
      );
      return;
    }
    if (!isTransactionPositive) {
      setLinkError(
        t("bankTransactions.positiveOnly", "Only positive transactions can be linked to receivables."),
      );
      return;
    }
    if (selectedIds.size === 0) {
      setLinkError(
        t("bankTransactions.selectReceivable", "Select at least one receivable journal entry to link."),
      );
      return;
    }
    if (selectedTotal <= amountEpsilon) {
      setLinkError(t("bankTransactions.invalidTotal", "Selected receivables must net to a positive amount."));
      return;
    }
    setIsLoading(true);
    setLinkError(null);
    try {
      await fetchJson(`/api/houses/${houseId}/bank-transactions/${transaction.id}/link`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          journalEntryIds: Array.from(selectedIds),
          channelId: channel,
        }),
      });
      await loadLinks();
      await loadSuggestions();
      onUpdated();
      onClose();
    } catch (linkError) {
      if (linkError instanceof Error && linkError.message === "missing_journal_entries") {
        setLinkError(
          t("bankTransactions.selectReceivable", "Select at least one receivable journal entry to link."),
        );
        return;
      }
      setLinkError(
        linkError instanceof Error
          ? linkError.message
          : t("bankTransactions.linkFailed", "Failed to link receivables"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const submitEntry = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isReadonly) {
      return;
    }
    setJournalEntryError(null);
    if (!journalDate) {
      setJournalEntryError(
        t("bankTransactions.missingDate", "This transaction is missing a date."),
      );
      return;
    }
    const parsedAmount = Number.parseFloat(journalEntryAmount);
    if (!Number.isFinite(parsedAmount)) {
      setJournalEntryError(t("bankTransactions.validAmount", "Enter a valid amount."));
      return;
    }
    if (Math.abs(parsedAmount) <= amountEpsilon) {
      setJournalEntryError(
        t("bankTransactions.amountGreaterThanZero", "Amount must be greater than zero."),
      );
      return;
    }
    if (Math.abs(parsedAmount) - Math.abs(remaining) > amountEpsilon) {
      setJournalEntryError(
        t("bankTransactions.exceedsRemaining", "Amount exceeds remaining transaction balance."),
      );
      return;
    }
    if (Math.sign(parsedAmount) !== Math.sign(remaining)) {
      setJournalEntryError(
        t(
          "bankTransactions.sameSignRemaining",
          "Amount must have the same sign as the remaining transaction.",
        ),
      );
      return;
    }
    const parsedAccountId = accountId ? Number.parseInt(accountId, 10) : Number.NaN;
    const selectedAccount =
      !Number.isNaN(parsedAccountId) ? (accounts.find((account) => account.id === parsedAccountId) ?? null) : null;
    if (isAmountSignUnexpectedForAccount(selectedAccount, parsedAmount)) {
      const displayName = selectedAccount
        ? (selectedAccount.labelKey ? t(selectedAccount.labelKey, selectedAccount.name) : selectedAccount.name) +
          " · " +
          selectedAccount.number
        : t("entries.account", "Account");
      const shouldContinue = window.confirm(
        t(
          "entries.amountSignWarning",
          "Warning: The amount sign looks unusual for {account}. Continue anyway?",
          { account: displayName },
        ),
      );
      if (!shouldContinue) {
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const journalEntryResponse = await fetchJson<{ id: number }>(
        `/api/houses/${houseId}/journal-entries`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            accountId: Number.isNaN(parsedAccountId) ? null : parsedAccountId,
            ledgerType: "actual",
            amount: parsedAmount,
            currency,
            journalDate,
            channel: null,
            channelRef: null,
            notes: notes.trim() || null,
          }),
        },
      );
      await fetchJson(`/api/houses/${houseId}/bank-transactions/${transaction.id}/links`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          journalEntryId: journalEntryResponse.id,
          amount: parsedAmount,
        }),
      });
      onUpdated();
      onClose();
    } catch (submitError) {
      setJournalEntryError(
        submitError instanceof Error
          ? submitError.message
          : t("bankTransactions.createEntryFailed", "Failed to create journal entry"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeLink = async (linkId: number) => {
    if (isReadonly) {
      return;
    }
    const shouldRemove = window.confirm(
      t(
        "bankTransactions.removeLinkConfirm",
        "Remove this link and delete the underlying journal entry? This cannot be undone.",
      ),
    );
    if (!shouldRemove) {
      return;
    }
    setIsLoading(true);
    setLinkError(null);
    try {
      await fetchJson(
        `/api/houses/${houseId}/bank-transactions/${transaction.id}/links/${linkId}`,
        { method: "DELETE" },
      );
      await loadLinks();
      onUpdated();
    } catch (removeError) {
      setLinkError(
        removeError instanceof Error
          ? removeError.message
          : t("bankTransactions.removeLinkFailed", "Failed to remove link"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <Card className="max-h-[calc(100vh-2rem)] w-full max-w-[95vw] overflow-hidden sm:max-w-3xl lg:max-w-5xl">
        <CardHeader className="relative">
          <CardTitle>{t("bankTransactions.linkTitle", "Link transaction")}</CardTitle>
          <button
            type="button"
            className="absolute right-4 top-4 text-xl text-muted transition hover:text-ink"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
          >
            ×
          </button>
        </CardHeader>
        <CardContent className="space-y-4 overflow-y-auto">
          {isReadonly ? (
            <p className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t("bankTransactions.linkReadonly", "This house is read-only. Linking is disabled.")}
            </p>
          ) : null}
          <div className="rounded-md border brand-border p-3 text-sm">
            <p className="font-medium text-ink">
              {transaction.transactionDate ?? "—"} · {transaction.description ?? "—"}
            </p>
            <p className="text-muted">
              {t("bankTransactions.linkSummary", "Amount: {amount} · Linked: {linked} · Remaining: {remaining}", {
                amount: formatAmount(transaction.amount, currency),
                linked: formatAmount(linkedTotal, currency),
                remaining: formatAmount(remaining, currency),
              })}
            </p>
            <p className="text-muted">
              {t("bankTransactions.channelBalance", "Channel: {channel} · Balance: {balance}", {
                channel: channelLabel ?? t("bankTransactions.notDetected", "Not detected"),
                balance: formatAmount(channelBalance, currency),
              })}
            </p>
          </div>
          <div className="rounded-md border brand-border p-3 text-sm">
            <p className="text-muted">
              {t(
                "bankTransactions.linkAllocation",
                "Assigned: {assigned} · Remaining: {remaining} · Balance applied: {balanceApplied} · Available: {available}",
                {
                  assigned: formatAmount(assignedToTransaction, currency),
                  remaining: formatAmount(remainingTransaction, currency),
                  balanceApplied: formatAmount(balanceApplied, currency),
                  available: formatAmount(availableRemaining, currency),
                },
              )}
            </p>
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={handleSmartLink}
                disabled={
                  isReadonly ||
                  !channel ||
                  isLoading ||
                  !isTransactionPositive ||
                  suggestions.length === 0
                }
              >
                {t("bankTransactions.smartLink", "Smart link")}
              </Button>
            </div>
          </div>
          {linkError ? <p className="text-sm text-error">{linkError}</p> : null}
          {isLoading ? (
            <p className="text-sm text-muted">{t("common.loading", "Loading...")}</p>
          ) : null}
          <div className="space-y-4">
            <div className="border-b brand-border">
              <nav
                className="-mb-px flex flex-wrap gap-4 text-sm font-medium"
                aria-label={t("bankTransactions.linkTabs", "Link tabs")}
              >
                <button
                  type="button"
                  className={`border-b-2 pb-2 ${
                    activeTab === "suggested"
                      ? "border-ink text-ink"
                      : "border-transparent text-muted hover:text-ink"
                  }`}
                  onClick={() => setActiveTab("suggested")}
                >
                  {t("bankTransactions.receivableLinks", "Receivable links")}
                </button>
                <button
                  type="button"
                  className={`border-b-2 pb-2 ${
                    activeTab === "journal-entry"
                      ? "border-ink text-ink"
                      : "border-transparent text-muted hover:text-ink"
                  }`}
                  onClick={() => setActiveTab("journal-entry")}
                >
                  {t("entries.createTitle", "Create journal entry")}
                </button>
                <button
                  type="button"
                  className={`border-b-2 pb-2 ${
                    activeTab === "links"
                      ? "border-ink text-ink"
                      : "border-transparent text-muted hover:text-ink"
                  }`}
                  onClick={() => setActiveTab("links")}
                >
                  {t("bankTransactions.linksTab", "Links")}
                </button>
              </nav>
            </div>
            {activeTab === "suggested" ? (
              <div>
                {!isTransactionPositive ? (
                  <p className="text-sm text-muted">
                    {t(
                      "bankTransactions.positiveOnly",
                      "Only positive transactions can be linked to receivables.",
                    )}
                  </p>
                ) : !channel ? (
                  <p className="text-sm text-muted">
                    {t(
                      "bankTransactions.channelNotDetectedDescription",
                      "No channel detected from the bank transaction description.",
                    )}
                  </p>
                ) : suggestions.length === 0 ? (
                  <p className="text-sm text-muted">
                    {t("bankTransactions.noUnsettled", "No unsettled journal entries found.")}
                  </p>
                ) : (
                  <div className="max-h-[50vh] overflow-auto rounded-md border brand-border sm:max-h-[360px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[56px]" />
                          <TableHead className="min-w-[160px]">
                            {t("receivables.bookingId", "Booking ID")}
                          </TableHead>
                          <TableHead className="min-w-[240px]">
                            {t("entries.account", "Account")}
                          </TableHead>
                          <TableHead className="min-w-[160px]">
                            {t("entries.journalDate", "Journal date")}
                          </TableHead>
                          <TableHead className="min-w-[160px]">
                            {t("receivables.created", "Created")}
                          </TableHead>
                          <TableHead className="text-right">
                            {t("bankTransactions.amount", "Amount")}
                          </TableHead>
                          <TableHead className="text-right">
                            {t("bankTransactions.remaining", "Remaining")}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {suggestions.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell className="w-[56px]">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(entry.id)}
                                onChange={() => toggleEntry(entry)}
                                aria-label={t("bankTransactions.selectReceivable", "Select receivable")}
                                disabled={isReadonly}
                              />
                            </TableCell>
                            <TableCell className="min-w-[160px]">
                              {entry.bookingId ?? "—"}
                            </TableCell>
                            <TableCell className="min-w-[240px]">
                              {entry.accountLabelKey
                                ? t(entry.accountLabelKey, entry.accountName ?? "")
                                : entry.accountName ?? t("entries.noAccount", "No account")}
                            </TableCell>
                            <TableCell className="min-w-[160px]">{entry.journalDate ?? "—"}</TableCell>
                            <TableCell className="min-w-[160px]">
                              {entry.createdAt.slice(0, 10)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatAmount(entry.amount, entry.currency)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatAmount(entry.remainingAmount, entry.currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    onClick={linkSelected}
                    disabled={
                      isReadonly ||
                      !channel ||
                      selectedIds.size === 0 ||
                      selectedTotal <= amountEpsilon ||
                      isLoading ||
                      !isTransactionPositive
                    }
                  >
                    {t("bankTransactions.linkSelected", "Link selected")}
                  </Button>
                </div>
              </div>
            ) : activeTab === "journal-entry" ? (
              <form className="space-y-4" onSubmit={submitEntry}>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-ink" htmlFor="transaction-account">
                      {t("entries.account", "Account")}
                    </label>
                    <select
                      id="transaction-account"
                      className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                      value={accountId}
                      onChange={(event) => setAccountId(event.target.value)}
                      disabled={isLoadingAccounts || isReadonly}
                    >
                      <option value="">{t("entries.noAccount", "No account")}</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {(account.labelKey ? t(account.labelKey, account.name) : account.name) +
                            " · " +
                            account.number}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-ink" htmlFor="transaction-entry-type">
                      {t("entries.ledgerType", "Ledger type")}
                    </label>
                    <Input id="transaction-entry-type" value={t("ledger.actual", "Actual")} disabled />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-ink" htmlFor="transaction-entry-date">
                      {t("entries.journalDate", "Journal date")}
                    </label>
                    <Input id="transaction-entry-date" type="date" value={journalDate} disabled />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-ink" htmlFor="transaction-amount">
                      {t("bankTransactions.amount", "Amount")}
                    </label>
                    <Input
                      id="transaction-amount"
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={journalEntryAmount}
                      onChange={(event) => {
                        setJournalEntryAmount(event.target.value);
                        setHasEditedAmount(true);
                      }}
                      disabled={isReadonly}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-ink" htmlFor="transaction-source">
                      {t("bankTransactions.channel", "Channel")}
                    </label>
                    <Input
                      id="transaction-source"
                      value={t("bankTransactions.channelValue", "Bank transaction")}
                      disabled
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-ink" htmlFor="transaction-notes">
                    {t("entries.notes", "Notes")}
                  </label>
                  <Input
                    id="transaction-notes"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder={t("entries.notesPlaceholder", "Optional notes")}
                    disabled={isReadonly}
                  />
                </div>
                {isLoadingAccounts ? (
                  <p className="text-sm text-muted">
                    {t("bankTransactions.loadingAccounts", "Loading accounts...")}
                  </p>
                ) : null}
                {journalEntryError ? <p className="text-sm text-error">{journalEntryError}</p> : null}
                <div className="flex justify-end gap-2">
                  <Button type="submit" disabled={isSubmitting || isLoadingAccounts || isReadonly}>
                    {isSubmitting
                      ? t("common.saving", "Saving...")
                      : t("entries.createTitle", "Create journal entry")}
                  </Button>
                </div>
              </form>
            ) : (
              <div>
                {!links.length ? (
                  <p className="text-sm text-muted">{t("bankTransactions.noLinks", "No links yet.")}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[120px]">
                          {t("receivables.bookingId", "Booking ID")}
                        </TableHead>
                        <TableHead className="min-w-[240px]">
                          {t("entries.account", "Account")}
                        </TableHead>
                        <TableHead className="min-w-[160px]">
                          {t("entries.journalDate", "Journal date")}
                        </TableHead>
                        <TableHead className="min-w-[160px]">
                          {t("receivables.created", "Created")}
                        </TableHead>
                        <TableHead className="text-right">
                          {t("bankTransactions.amount", "Amount")}
                        </TableHead>
                        <TableHead className="text-right">
                          {t("bankTransactions.remaining", "Remaining")}
                        </TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {links.map((link) => {
                        const linkCurrency = link.journalEntryCurrency ?? currency;
                        const journalEntryAmount = link.journalEntryAmount ?? 0;
                        const journalEntryLinkedAmount = link.journalEntryLinkedAmount ?? 0;
                        const remainingAmount = getReceivableRemainingAmount(
                          journalEntryAmount,
                          journalEntryLinkedAmount,
                        );
                        return (
                          <TableRow key={link.id}>
                            <TableCell className="min-w-[120px]">
                              {link.bookingId ?? "—"}
                            </TableCell>
                            <TableCell className="min-w-[240px]">{formatAccountLabel(link)}</TableCell>
                            <TableCell className="min-w-[160px]">
                              {link.journalEntryDate ?? "—"}
                            </TableCell>
                            <TableCell className="min-w-[160px]">
                              {link.journalEntryCreatedAt ? link.journalEntryCreatedAt.slice(0, 10) : "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatAmount(journalEntryAmount, linkCurrency)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatAmount(remainingAmount, linkCurrency)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeLink(link.id)}
                                disabled={isReadonly}
                              >
                                {t("common.remove", "Remove")}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
